/**
 * 预算管理服务
 */
import api from './api';
import { 
  ApiResponse, 
  PaginatedResponse,
  BudgetDetail, 
  BudgetBreakdown, 
  BudgetMode,
  BudgetAdjustment,
  BudgetAdjustmentStats,
  RevenueBreakdown,
} from '../types';

/**
 * 获取项目预算详情
 */
export const getBudgetDetail = async (projectId: string): Promise<ApiResponse<BudgetDetail>> => {
  const response = await api.get(`/budget/${projectId}`);
  return response;
};

/**
 * 更新项目预算
 */
export interface UpdateBudgetData {
  budget?: number;
  budgetBreakdown?: Partial<BudgetBreakdown>;
  revenueTotal?: number;
  revenueBreakdown?: Partial<RevenueBreakdown>;
  budgetMode?: BudgetMode;
  reason?: string;
}

export const updateBudget = async (
  projectId: string, 
  data: UpdateBudgetData
): Promise<ApiResponse<any>> => {
  const response = await api.put(`/budget/${projectId}`, data);
  return response;
};

/**
 * 锁定项目预算
 */
export const lockBudget = async (projectId: string): Promise<ApiResponse<any>> => {
  const response = await api.post(`/budget/${projectId}/lock`);
  return response;
};

/**
 * 解锁项目预算
 */
export const unlockBudget = async (
  projectId: string, 
  reason: string
): Promise<ApiResponse<any>> => {
  const response = await api.post(`/budget/${projectId}/unlock`, { reason });
  return response;
};

/**
 * 获取预算调整历史
 */
export interface BudgetHistoryParams {
  page?: number;
  limit?: number;
}

export const getBudgetHistory = async (
  projectId: string,
  params?: BudgetHistoryParams
): Promise<PaginatedResponse<BudgetAdjustment>> => {
  const response = await api.get(`/budget/${projectId}/history`, { params });
  return response;
};

/**
 * 获取预算调整统计
 */
export const getBudgetStats = async (
  projectId: string
): Promise<ApiResponse<BudgetAdjustmentStats>> => {
  const response = await api.get(`/budget/${projectId}/stats`);
  return response;
};

/**
 * 批量获取多个项目的预算概览
 */
export interface BudgetOverviewItem {
  projectId: string;
  projectName: string;
  budget: number;
  calculatedTotal: number;
  difference: number;
  revenueTotal?: number;
  calculatedRevenueTotal?: number;
  revenueDifference?: number;
  budgetVersion?: number;
  budgetMode: BudgetMode;
  budgetLocked: boolean;
}

export const getBudgetOverview = async (
  projectIds: string[]
): Promise<ApiResponse<BudgetOverviewItem[]>> => {
  const response = await api.post('/budget/overview', { projectIds });
  return response;
};

/**
 * 计算预算成分总和
 */
export const calculateBudgetTotal = (breakdown: Partial<BudgetBreakdown>): number => {
  return (breakdown.labor || 0) + 
         (breakdown.equipment || 0) + 
         (breakdown.material || 0) + 
         (breakdown.coordination || 0) + 
         (breakdown.contingency || 0);
};

/**
 * 获取预算使用率
 */
export interface BudgetUsageRate {
  category: string;
  budget: number;
  used: number;
  rate: number;
  status: 'normal' | 'warning' | 'danger';
}

export const calculateBudgetUsage = (
  breakdown: BudgetBreakdown,
  actualCosts: {
    labor: number;
    equipment: number;
    material: number;
    coordination: number;
  }
): BudgetUsageRate[] => {
  const categories = [
    { key: 'labor', label: '人工' },
    { key: 'equipment', label: '机械' },
    { key: 'material', label: '材料' },
    { key: 'coordination', label: '协调' }
  ];
  
  return categories.map(cat => {
    const budget = breakdown[cat.key as keyof BudgetBreakdown] || 0;
    const used = actualCosts[cat.key as keyof typeof actualCosts] || 0;
    const rate = budget > 0 ? (used / budget) * 100 : 0;
    
    let status: 'normal' | 'warning' | 'danger' = 'normal';
    if (rate >= 100) {
      status = 'danger';
    } else if (rate >= 80) {
      status = 'warning';
    }
    
    return {
      category: cat.label,
      budget,
      used,
      rate,
      status
    };
  });
};

/**
 * 格式化预算调整类型
 */
export const formatAdjustmentType = (type: string): string => {
  const typeMap: Record<string, string> = {
    initial: '初始设置',
    increase: '增加预算',
    decrease: '减少预算',
    reallocation: '重新分配',
    correction: '修正错误'
  };
  return typeMap[type] || type;
};

/**
 * 获取预算调整类型颜色
 */
export const getAdjustmentTypeColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    initial: 'blue',
    increase: 'green',
    decrease: 'red',
    reallocation: 'orange',
    correction: 'purple'
  };
  return colorMap[type] || 'default';
};

export default {
  getBudgetDetail,
  updateBudget,
  lockBudget,
  unlockBudget,
  getBudgetHistory,
  getBudgetStats,
  getBudgetOverview,
  calculateBudgetTotal,
  calculateBudgetUsage,
  formatAdjustmentType,
  getAdjustmentTypeColor
};
